import React, { useState } from "react";
import { Radio } from "@mui/material";
import { Provider } from "../../../../data/entities";
import { ProviderTableColumns, Search } from "../../../../configs/provider";
import { api } from "../../../../shared/hooks/api";
import PaginationBar from "../../../../shared/components/PaginationBar";
import SearchBar from "../../../../shared/components/SearchBar";
import CustomTable from "../../../../shared/components/CustomTable";
import { StyledTableCell } from "../../../../shared/components/CustomTable/Styled";
import { DEFAULT_PAGE_SIZE } from "../../../../configs/apiConfig";
import { StyledNavLink } from "../../../../shared/styled/TableStyles";
import { StyledButton } from "../../../../shared/styled/TitleBar";
import { color } from "../../../../shared/styles";
import { ContentWrapper, ExpandableContent, ProviderTableLayout, SelectionActions, StyledTableRow } from "./Styled";

interface ProviderRowProps {
    item: Provider;
    extraRowProps: any;
}

export const getDefaultContactName = (provider: Provider) => {
    const contacts = Array.isArray(provider.contact_list) ? provider.contact_list : [];
    const defaultContact = contacts.find((item: any) => item?.contact?.is_default)?.contact;
    const fallbackContact = contacts.find((item: any) => item?.contact)?.contact;
    return defaultContact?.name || fallbackContact?.name || "Chưa có";
};

const ProviderRow = ({ item, extraRowProps }: ProviderRowProps) => {
    return (
        <StyledTableRow
            style={{ backgroundColor: extraRowProps.confirmSelection && extraRowProps.selectedProviderId === item.id ? color.status.INFO_LIGHT : undefined }}
            aria-selected={extraRowProps.confirmSelection ? extraRowProps.selectedProviderId === item.id : undefined}
            onClick={() => extraRowProps.selectProvider(item)}
        >
            {extraRowProps.confirmSelection && (
                <StyledTableCell align="center" sx={{ width: 44, minWidth: 44, maxWidth: 44, padding: "2px 4px" }}>
                    <Radio
                        name="outsource-provider"
                        size="small"
                        checked={extraRowProps.selectedProviderId === item.id}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => extraRowProps.selectProvider(item)}
                        inputProps={{ "aria-label": `Chọn ${item.name}` }}
                        sx={{ padding: "5px", "& .MuiSvgIcon-root": { fontSize: 14 } }}
                    />
                </StyledTableCell>
            )}
            <StyledTableCell align="left">
                <StyledNavLink
                    to={`/providers/${item.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                >
                    {item.provider_code ? item.provider_code : item.id}
                </StyledNavLink>
            </StyledTableCell>
            <StyledTableCell align="left">{item.name}</StyledTableCell>
            <StyledTableCell align="left">{item.phone}</StyledTableCell>
            <StyledTableCell align="left">{item.email}</StyledTableCell>
            <StyledTableCell align="left">{getDefaultContactName(item)}</StyledTableCell>
            <StyledTableCell align={extraRowProps.confirmSelection ? "center" : "left"}>
                {item.number_of_outsource_materials ?? 0}
            </StyledTableCell>
        </StyledTableRow>
    );
};

interface SelectProvidersProps {
    setSelectedProvider: Function;
    title?: string;
    searchText?: string;
    tableColumns?: any[];
    queryParams?: Record<string, any>;
    confirmSelection?: boolean;
    onCancelSelection?: () => void;
    isSubmitting?: boolean;
}

const SelectProviders = ({
    setSelectedProvider,
    title = "Vui lòng lựa chọn nhà cung cấp",
    searchText = Search.searchText,
    tableColumns = ProviderTableColumns,
    queryParams = {},
    confirmSelection = false,
    onCancelSelection,
    isSubmitting = false,
}: SelectProvidersProps) => {
    const [{ data, variables }, fetchProviders] = api.get(
        "/materials/providers/",
        { page_size: DEFAULT_PAGE_SIZE, ...queryParams },
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedProvider, setCurrentSelection] = useState<Provider | null>(null);
    const providers = data && data.results ? data.results : [];
    const totalItems = data && data.count ? data.count : 0;
    const pageSize = variables && variables.page_size ? variables.page_size : DEFAULT_PAGE_SIZE;
    const providerTableColumns = tableColumns.some((column) =>
        column.text === "Số vật tư gia công" || column.text === "Vật tư gia công"
    )
        ? tableColumns
        : [...tableColumns, {
            text: confirmSelection ? "Vật tư gia công" : "Số vật tư gia công",
            icon: <></>,
            ...(confirmSelection ? { sx: { "& > div": { justifyContent: "center" } } } : {}),
        }];
    const columns = confirmSelection
        ? [{ text: "", icon: <></>, width: "44px", sx: { minWidth: "44px", maxWidth: "44px" } }, ...providerTableColumns]
        : providerTableColumns;

    const selectProvider = (provider: Provider) => {
        if (confirmSelection) setCurrentSelection(provider);
        else setSelectedProvider(provider);
    };

    return (
        <ContentWrapper>
            <ExpandableContent>
                {title}
                <SearchBar
                    placeholder={searchText}
                    fetchItems={(variables: any) => fetchProviders({ ...queryParams, ...variables })}
                />
                <ProviderTableLayout $withRadio={confirmSelection}>
                    <CustomTable
                        itemList={providers}
                        RowElement={ProviderRow}
                        TableColumns={columns}
                        extraRowProps={{ selectProvider, confirmSelection, selectedProviderId: selectedProvider?.id }}
                    />
                </ProviderTableLayout>
            </ExpandableContent>

            <PaginationBar
                totalItems={totalItems}
                pageSize={pageSize}
                fetchItems={(variables: any) => fetchProviders({ ...queryParams, ...variables })}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            {confirmSelection && (
                <SelectionActions>
                    <StyledButton variant="outlined" onClick={onCancelSelection} disabled={isSubmitting}>
                        Huỷ
                    </StyledButton>
                    <StyledButton
                        variant="contained"
                        disabled={!selectedProvider || isSubmitting}
                        onClick={() => selectedProvider && setSelectedProvider(selectedProvider)}
                    >
                        Tạo đơn hàng
                    </StyledButton>
                </SelectionActions>
            )}
        </ContentWrapper>
    );
};

export default SelectProviders;
